/**
 * Copyright (c) 2026, CheckPlus Limited
 */
import React, { Component } from "react";
import "./Transactions.css";
import { simpleFetch } from "../../restWrapper";
import InfiniteScroll from "react-infinite-scroll-component";
import { Link } from "react-router-dom";
import PropTypes from "prop-types";
import { NumericFormat } from "react-number-format";
import { Helmet } from "react-helmet";
import { getOrganizationID, validatePlatformMatch, resolveThemeColors } from "../../assets/helper";
import { PLATFORM } from "../../config";
import { rememberOrgTitle } from "../../assets/orgTitle";

/*
 * The donor's transactions, one tab per payment status. The three tabs used
 * to be three pages, so every switch refetched the organization and the list
 * from scratch; now the organization is fetched once and each tab's list is
 * kept once loaded (CP-413).
 */
export const TABS = {
  completed: { path: "/Transactions", status: "complete" },
  scheduled: { path: "/Transactions/scheduled", status: "scheduled" },
  reauthenticate: { path: "/Transactions/reauthenticate", status: "reauthenticate" },
};
const TAB_ORDER = ["completed", "scheduled", "reauthenticate"];

const tabFromLocation = (location) => {
  const segment = ((location && location.pathname) || "").split("/")[2];
  const tab = segment && segment.toLowerCase();
  return TABS[tab] ? tab : "completed";
};

const emptyList = () => ({
  records: [],
  lastId: 0,
  hasMore: true,
  fetching: false,
  loaded: false,
});

const formatRecordDate = (scheduledDate) => {
  const rDate = new Date(scheduledDate);

  const d = rDate.getDate();
  const m = rDate.getMonth() + 1;
  const y = rDate.getFullYear();
  const h = rDate.getHours();
  const min = (rDate.getMinutes() < 10 ? "0" : "") + rDate.getMinutes();

  return y + "/" + m + "/" + d + " " + h + ":" + min;
};

class Transactions extends Component {
  constructor(props) {
    super(props);
    const { subdomain, domain, isCustomDomain } = getOrganizationID(window.location.href);
    this.state = {
      subdomain: subdomain,
      domain: domain,
      isCustomDomain: isCustomDomain,
      buttonColor: "",
      headerColor: "",
      orgId: "",
      orgName: "",
      logo: "",
      orgFailed: false,
      lists: {
        completed: emptyList(),
        scheduled: emptyList(),
        reauthenticate: emptyList(),
      },
      frequencyMenuOpen: false,
      // Sent here to open one payment: stay behind the loader until it opens,
      // so the list doesn't flash up first (CP-406)
      openingPayment: Boolean(
        props.location && props.location.state && props.location.state.openPaymentId
      ),
    };
    this.fetchList = this.fetchList.bind(this);
    this.toggleFrequencyMenu = this.toggleFrequencyMenu.bind(this);
    this.openRecord = this.openRecord.bind(this);
  }

  componentDidMount() {
    simpleFetch("/donors/organization", "GET", {
      subDomain: this.state.isCustomDomain ? this.state.domain : this.state.subdomain,
      isCustomDomain: this.state.isCustomDomain,
    }).then((res) => {
      rememberOrgTitle(res.body.organizationObj.organizationName);

      // Validate platform matches organization type
      const platformMatch = validatePlatformMatch(
        res.body.organizationObj.organizationType,
        PLATFORM
      );

      if (!platformMatch) {
        this.props.history.push({ pathname: "/" + this.props.language });
        return;
      }

      this.setState({
        buttonColor: res.body.organizationObj.buttonColor,
        headerColor: res.body.organizationObj.headerColor,
        orgId: res.body.id,
        orgName: res.body.organizationObj.organizationName,
        logo: res.body.organizationObj.logo,
      }, () => {
        // Callback ensures orgId is set before fetching data (React 18 fix)
        this.fetchList(this.currentTab());
        const { state } = this.props.location || {};
        if (state && state.openPaymentId) this.openPayment(state.openPaymentId);
      });
    }).catch(() => {
      // Never left behind the loader, nor on placeholders that never fill
      this.setState({ openingPayment: false, orgFailed: true });
    });
  }

  componentDidUpdate(prevProps) {
    // A tab is loaded the first time it is shown, then kept
    const tab = this.currentTab();
    if (tab !== tabFromLocation(prevProps.location) && !this.state.lists[tab].loaded) {
      this.fetchList(tab);
    }
  }

  currentTab() {
    return tabFromLocation(this.props.location);
  }

  updateList(tab, changes) {
    this.setState((prev) => ({
      lists: { ...prev.lists, [tab]: { ...prev.lists[tab], ...changes(prev.lists[tab]) } },
    }));
  }

  fetchList(tab) {
    const list = this.state.lists[tab];
    if (list.fetching) return false;
    if (!this.state.orgId) return false;
    this.updateList(tab, () => ({ fetching: true }));

    let findParams = {
      organizationViewId: this.state.orgId,
      status: TABS[tab].status,
    };

    if (list.lastId) findParams.lastId = list.lastId;

    simpleFetch("/donors/payments", "GET", findParams).then((res) => {
      const records = res.body;
      if (!Array.isArray(records) || records.length === 0) {
        this.updateList(tab, () => ({ fetching: false, loaded: true, hasMore: false }));
        return;
      }
      this.updateList(tab, (current) => ({
        fetching: false,
        loaded: true,
        hasMore: true,
        lastId: records[records.length - 1].id,
        records: current.records.concat(records),
      }));
    }).catch(() => {
      this.updateList(tab, () => ({ fetching: false, loaded: true, hasMore: false }));
    });
  }

  /*
   * Opens one payment's record, for a donor sent here to see it: a
   * reauthentication completes the payment it replaces, and its record has
   * the receipt (CP-406). The request is cleared first, so coming back from
   * the record shows the list instead of opening it again.
   */
  openPayment(paymentId) {
    this.props.history.replace({ pathname: this.props.location.pathname });
    simpleFetch("/donors/payments/" + encodeURIComponent(paymentId), "GET")
      .then((res) => {
        if (res && res.status === 200 && res.body && res.body.status === "complete") {
          this.openRecord(res.body);
          return;
        }
        // Can't open it: show the list to pick it from
        this.setState({ openingPayment: false });
      })
      .catch(() => {
        this.setState({ openingPayment: false });
      });
  }

  openRecord(data) {
    this.props.history.push({
      pathname: "/DonationInfoProcessed",
      state: {
        buttonColor: this.state.buttonColor,
        headerColor: this.state.headerColor,
        orgName: this.state.orgName,
        logo: this.state.logo,
        data: data,
        date: formatRecordDate(data.scheduledDate),
      },
    });
  }

  openDetail(pathname, data) {
    this.props.history.push({
      pathname: pathname,
      state: {
        buttonColor: this.state.buttonColor,
        headerColor: this.state.headerColor,
        orgName: this.state.orgName,
        paymentId: data.id,
      },
    });
  }

  toggleFrequencyMenu() {
    this.setState((prev) => ({
      frequencyMenuOpen: !prev.frequencyMenuOpen,
    }));
  }

  tabLabel(tab) {
    const languageObj = this.props.languageObj;
    const fallback = { completed: "Completed", scheduled: "Scheduled", reauthenticate: "Re-authenticate" };
    return languageObj[tab] || fallback[tab];
  }

  themeStyle() {
    const hasHeaderColor =
      typeof this.state.headerColor === "string" &&
      this.state.headerColor.length > 0;
    const resolved = hasHeaderColor
      ? resolveThemeColors(this.state.headerColor, this.state.buttonColor)
      : { headerColor: "", buttonColor: "" };
    const accentColor = hasHeaderColor
      ? `#${resolved.headerColor}`
      : "#606266";
    const accentColorSoft = hasHeaderColor
      ? `#${resolved.headerColor}1A`
      : "#6062661A";
    const buttonColor =
      resolved.buttonColor && resolved.buttonColor.length > 0
        ? `#${resolved.buttonColor}`
        : accentColor;

    return {
      "--donation-frame-accent": accentColor,
      "--donation-frame-accent-soft": accentColorSoft,
      "--donation-arrow-color": accentColor,
      "--donation-arrow-hover-color": buttonColor,
      "--donation-text-color": accentColor,
      "--donation-logo-color": accentColor,
    };
  }

  renderAmount(label, amount) {
    return (
      <div className="Transactions-amount">
        {label}: HK
        <NumericFormat
          decimalSeparator="."
          displayType="text"
          valueIsNumericString={true}
          value={amount}
          allowNegative={false}
          thousandSeparator={true}
          prefix={"$"}
          fixedDecimalScale={true}
          decimalScale={2}
        />
      </div>
    );
  }

  /*
   * One card. The tabs differ only in the badge, the lines under the title,
   * the pill's label and where a click goes.
   */
  renderCard(data, { badge, details, pillLabel, onClick }) {
    const languageObj = this.props.languageObj.renderRecord;
    const logoStyle = {
      backgroundImage: "url(" + this.state.logo + ")",
      backgroundPosition: "center",
      backgroundRepeat: "no-repeat",
      backgroundSize: "contain",
      border: "none",
    };

    return (
      <div
        className="Transactions-frame"
        key={"paymentID" + data.id}
        onClick={onClick}
        style={this.themeStyle()}
      >
        <div className="Transactions-cardHeader">
          {badge}
          <div className="Transactions-dateTime">{formatRecordDate(data.scheduledDate)}</div>
        </div>

        <div className="Transactions-cardContent">
          <div className="Transactions-logoWrapper">
            <div className="Transactions-logo" style={logoStyle}></div>
          </div>
          <div className="Transactions-transactionInfo">
            <div className="Transactions-title">
              {this.state.orgName} - {data.projectName}
            </div>
            {details}
            <div className="Transactions-amountRow">
              {this.renderAmount(languageObj.amount || "Amount", data.amount)}
              <div className="Transactions-arrowPill">
                <span>{pillLabel}</span>
                <div className="Transactions-nextArrow" />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  renderCompleted(data) {
    const languageObj = this.props.languageObj.renderRecord;
    const receiptNumberLabel = languageObj.receiptNumber || "Receipt#";

    return this.renderCard(data, {
      badge: (
        <div className="Transactions-statusBadge">
          {languageObj.completed || "Completed"}{data.receiptVoidedAt && <> · {languageObj.receiptVoided}</>}
        </div>
      ),
      details: data.organizationReceiptId && (
        <div className="Transactions-receiptNumber">
          {receiptNumberLabel}: {data.organizationReceiptId}
        </div>
      ),
      pillLabel:
        languageObj.viewDetails ||
        languageObj.details ||
        languageObj.view ||
        "Details",
      onClick: () => this.openRecord(data),
    });
  }

  renderScheduled(data) {
    const languageObj = this.props.languageObj.renderRecord;

    const getFrequencyLabel = () => {
      let label = languageObj[data.recurringInterval] || "";

      if ((data.recurringInterval === "monthly" || data.recurringInterval === "quarterly") && data.recurringDay) {
        if (languageObj.dayUnit) {
          // Chinese format: "第21日"
          label += ` (${languageObj.day}${data.recurringDay}${languageObj.dayUnit})`;
        } else {
          // English format: "Day 21"
          label += ` (${languageObj.day} ${data.recurringDay})`;
        }
      }

      return label;
    };

    const getDurationLabel = () => {
      if (data.recurringEndDate) {
        return `${languageObj.until} ${new Date(data.recurringEndDate).toLocaleDateString()}`;
      }
      return languageObj.untilCancel;
    };

    return this.renderCard(data, {
      badge: (
        <div className={`Transactions-recurringBadge ${data.isRecurring ? "recurring" : "one-time"}`}>
          {data.isRecurring ? languageObj.recurring : languageObj.oneTime}
        </div>
      ),
      details: data.isRecurring && (
        <div className="Transactions-details">
          <div className="Transactions-detailItem">
            <span role="img" aria-label="calendar">🗓️</span>
            {getFrequencyLabel()}
          </div>
          <div className="Transactions-detailItem">
            <span role="img" aria-label="duration">♾️</span>
            {getDurationLabel()}
          </div>
          {data.recurringCount !== undefined && data.recurringCount > 0 && (
            <div className="Transactions-detailItem">
              {languageObj.processed} {data.recurringCount} {languageObj.time}
            </div>
          )}
        </div>
      ),
      pillLabel: languageObj.viewDetails || languageObj.details || "Details",
      onClick: () => this.openDetail("/DonationInfoRecurring", data),
    });
  }

  renderReauthenticate(data) {
    const languageObj = this.props.languageObj.renderRecord;

    return this.renderCard(data, {
      badge: (
        <div className="Transactions-statusBadge">
          <span role="img" aria-label="reauthenticate">
            🔒
          </span>
          {languageObj.reauthenticationRequired || "Re-authentication required"}
        </div>
      ),
      details: null,
      pillLabel: languageObj.reauthenticate || "Re-authenticate",
      onClick: () => this.openDetail("/DonationInfoReauthenticate", data),
    });
  }

  renderRecord(tab, data) {
    if (tab === "scheduled") return this.renderScheduled(data);
    if (tab === "reauthenticate") return this.renderReauthenticate(data);
    return this.renderCompleted(data);
  }

  renderTab(tab, activeTab) {
    const className = `Transactions-tab Transactions-tab--${tab}`;
    const content = (
      <React.Fragment>
        <div className={`Transactions-tabIcn Transactions-tabIcn--${tab}`} />
        <div className="Transactions-tabTxt">{this.tabLabel(tab)}</div>
      </React.Fragment>
    );

    if (tab === activeTab) {
      return (
        <div key={tab} className={className + " is-active"} aria-current="page">
          {content}
        </div>
      );
    }
    // replace: Back leaves the page rather than stepping through tabs
    return (
      <Link
        key={tab}
        to={TABS[tab].path}
        replace
        className={className}
        onClick={() => this.setState({ frequencyMenuOpen: false })}
      >
        {content}
      </Link>
    );
  }

  renderSkeleton() {
    return (
      <div
        className="Transactions-skeletonList"
        data-testid="transactions-loading"
        role="status"
        aria-label={this.props.languageObj.loading || "Loading…"}
      >
        {[0, 1, 2].map((i) => (
          <div className="Transactions-skeleton" key={i} aria-hidden="true">
            <div className="Transactions-skeletonHeader">
              <div className="Transactions-skeletonBar Transactions-skeletonBar--badge" />
              <div className="Transactions-skeletonBar Transactions-skeletonBar--date" />
            </div>
            <div className="Transactions-skeletonContent">
              <div className="Transactions-skeletonLogo" />
              <div className="Transactions-skeletonLines">
                <div className="Transactions-skeletonBar Transactions-skeletonBar--title" />
                <div className="Transactions-skeletonBar Transactions-skeletonBar--amount" />
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  renderList(tab, list, scrollBarStyle) {
    if (this.state.orgFailed) return null;
    // Placeholders until the tab's first page is in
    if (!list.loaded) return this.renderSkeleton();

    if (list.records.length === 0) {
      const languageObj = this.props.languageObj;
      const key = "empty" + tab.charAt(0).toUpperCase() + tab.slice(1);
      return (
        <div className="Transactions-empty" data-testid="transactions-empty">
          {languageObj[key]}
        </div>
      );
    }

    return (
      <InfiniteScroll
        key={tab}
        dataLength={list.records.length}
        next={() => this.fetchList(tab)}
        hasMore={list.hasMore}
        loader={<div className="Transactions-moreLoader" role="status" aria-label={this.props.languageObj.loading || "Loading…"} />}
        style={scrollBarStyle}
      >
        {list.records.map((record) => this.renderRecord(tab, record))}
      </InfiniteScroll>
    );
  }

  render() {
    if (this.state.openingPayment) {
      const { RenderFullPageLoading } = this.props;
      return RenderFullPageLoading ? <RenderFullPageLoading /> : null;
    }

    const languageObj = this.props.languageObj;
    const tab = this.currentTab();
    const list = this.state.lists[tab];

    const scrollBarStyle = {
      overflow: "inherit",
      marginBottom: "100px",
    };

    // The page shows straight away, loading in place (CP-414); the org's
    // colour fades in once known rather than starting from a wrong one
    const orgReady = Boolean(this.state.orgId);
    const headerStyle = this.state.headerColor
      ? { background: "#" + this.state.headerColor }
      : {};

    return (
      <div
        className="Transactions-view"
        data-testid="transactions-view"
        data-tab={tab}
        data-org-ready={orgReady}
      >
        <Helmet>
          <title>{this.state.orgName}</title>
        </Helmet>
        <div
          className={`Transactions-bg ${this.state.headerColor ? "is-ready" : ""}`}
          style={headerStyle}
        />
        <div className="Transactions-container">
          <div className="Transactions-header">
            <Link to="/">
              <div className="Transactions-backContainer">
                <div className="Transactions-back">
                  <div className="Transactions-backBtn"></div>
                  <div className="Transactions-backText">
                    {languageObj.backTxt}
                  </div>
                </div>
              </div>
            </Link>

            <div className={`Transactions-frequencyContainer ${this.state.frequencyMenuOpen ? "is-open" : ""}`}>
              <Link to="/" className="Transactions-frequencyBack">
                <div className="Transactions-frequencyBackIcn" />
                <span>{languageObj.backTxt}</span>
              </Link>
              <button
                type="button"
                className="Transactions-frequencyToggle"
                onClick={this.toggleFrequencyMenu}
                aria-expanded={this.state.frequencyMenuOpen}
                aria-label={languageObj.statusToggle || "Toggle status filter"}
              >
                <div className={`Transactions-tabIcn Transactions-tabIcn--${tab}`} />
                <span>{this.tabLabel(tab)}</span>
                <span className="Transactions-frequencyToggleIcon" />
              </button>
              <div className="Transactions-frequencyCurrent">
                {this.tabLabel(tab)}
              </div>
              <div className="Transactions-frequency">
                {TAB_ORDER.map((t) => this.renderTab(t, tab))}
              </div>
            </div>

            <div className="Transactions-btn">{languageObj.btn}</div>
          </div>

          <div className="Transactions-main" aria-busy={!list.loaded}>
            {this.renderList(tab, list, scrollBarStyle)}
          </div>
        </div>
      </div>
    );
  }
}

Transactions.propTypes = {
  language: PropTypes.string,
  languageObj: PropTypes.object,
  logout: PropTypes.func,
  RenderFullPageLoading: PropTypes.elementType,
};

export default Transactions;
